// Cache for the base64 encoded fonts to avoid re-fetching
import { asCached, blob2base64 } from '~/helper'
import type { Base64DataUrl } from '~/helper'

const LOG = false

export const log = LOG ? console.log : (..._args: any[]) => {}
export const CSS_URL_PATTERN = /url\(\s*["']?([^"'()]+)["']?\s*\)/gi
export const FONT_FILE_PATTERN = /\.(woff2|woff|ttf|eot)(?:[?#][^)]*)*$/i

/**
 * Extracts font URLs from CSS text
 * Returns an array of font URLs found in the CSS
 */
export function extractFontUrls(cssText: string): string[] {
  const fontUrls: string[] = []

  // Pattern to match url() declarations in CSS
  let match
  while ((match = CSS_URL_PATTERN.exec(cssText)) !== null) {
    const url = match[1]
    // Only include font files (woff2, woff, ttf, eot)
    if (FONT_FILE_PATTERN.test(url)) {
      fontUrls.push(url)
    }
  }

  return fontUrls
}

/**
 * Converts a relative URL to an absolute URL based on the current page
 */
export function resolveUrl(url: string, baseUrl: string = window.location.href): string {
  // If it's already absolute, return as-is
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:') || url.startsWith('blob:')) {
    return url
  }

  return new URL(url, baseUrl).href
}

/**
 * Fetches an asset (font/image) and converts it to a base64 data URL
 */
export async function fetchAssetAsBase64(url: string): Promise<Base64DataUrl> {
  try {
    const resolvedUrl = resolveUrl(url)
    const response = await fetch(resolvedUrl)
    if (!response.ok) {
      throw new Error(`Failed to fetch asset! ${response.status} ${response.statusText}`)
    }

    // Get the font as array buffer
    const blob = await response.blob()
    return blob2base64(blob)
  } catch (error) {
    console.warn('Could not fetch asset:', url, error)
    throw error
  }
}

export const imagesAsBase64 = asCached(fetchAssetAsBase64)
export const fontsAsBase64 = asCached(fetchAssetAsBase64)

/**
 * Main function to embed fonts in CSS for PDF generation
 * Processes CSS text and replaces font URLs with base64 data URLs
 */
export async function embedFontsAsBase64(document: Document, fonts: (string | RegExp)[] = [/.*/]): Promise<string> {
  const style = document.head.querySelector('style')
  if (!style) {
    return ''
  }
  const cssText = style.textContent || ''

  // Extract all font URLs from the CSS
  const fontUrls = Array.from(new Set(extractFontUrls(cssText))).filter((fontUrl) =>
    fonts.some((font) => {
      return font instanceof RegExp ? fontUrl.match(font) : fontUrl.includes(font)
    }),
  )
  log('Found font URLs in CSS:', fontUrls)

  let replacedCSS = cssText

  // Process each font URL
  const promises = fontUrls.map(async (fontUrl) => {
    try {
      // Skip if it's already base64, empty, or a blob URL
      if (shouldFontBeSkipped(fontUrl)) {
        return
      }

      // Fetch and convert font to base64
      const dataUrl = await fontsAsBase64.get(fontUrl)

      // Replace the original URL with the data URL in CSS
      const escapedUrl = fontUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const urlPattern = new RegExp(`url\\(\\s*["']?${escapedUrl}["']?\\s*\\)`, 'gi')
      replacedCSS = replacedCSS.replace(urlPattern, `url("${dataUrl}")`)

      log('Embedded font:', fontUrl)
    } catch (error) {
      console.warn('Failed to embed font:', fontUrl, error)
    }
  })
  await Promise.allSettled(promises)
  style.textContent = replacedCSS
  return replacedCSS
}

/**
 * Selects all <img> tags within a specific HTML element and replaces their src with a Base64 data URL.
 * * @param container - The parent HTML element to search within. Defaults to document.body.
 */
export async function embedImagesAsBase64(container: HTMLElement = document.body): Promise<void> {
  // Only query images inside the provided container
  const images = Array.from(container.querySelectorAll('img'))
  log('Found images in container:', images)

  const promises = images.map(async (img) => {
    try {
      const imgUrl = img.src

      if (shouldImageBeSkipped(img)) {
        return
      }
      img.src = await imagesAsBase64.get(imgUrl)
      log('Embedded image:', imgUrl)
    } catch (error) {
      console.warn(`Failed to embed image: ${img.src}`, error)
    }
  })

  // Run all conversions concurrently
  await Promise.allSettled(promises)
}

/**
 * Skip if it's already empty, base64, or a blob URL
 */
export function shouldImageBeSkipped(img: HTMLImageElement) {
  if (!img.src) return true
  if (img.src.startsWith('data:') || img.src.startsWith('blob:')) return true
  return false
}

/**
 * Skip if it's already empty or base64
 */
export function shouldFontBeSkipped(url: string) {
  if (!url) return true
  if (url.startsWith('data:') || url.startsWith('blob:')) return true
  return false
}
